import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Target, Scale, Handshake, Layers, Landmark, TrendingUp, BrainCircuit, Compass } from "lucide-react";
import Seo from "@/components/common/Seo";
import PageHeader from "@/components/common/PageHeader";
import { Reveal, KineticHeadingInView } from "@/components/common/Reveal";
import CTASection from "@/components/common/CTASection";
import { GEOGRAPHY } from "@/data/content";
import { IMG } from "@/data/images";

const PORTRAIT = "/aftab-about-speaking.jpg";

const CREDENTIALS = [
  "27+ years leading enterprise technology in growing and complex organisations",
  "Executive Technology Advisor and Fractional CIO to CEOs, boards and business owners",
  "Independent of vendors, platforms and commercial incentives",
  "Accountable from the decision through to the business result",
];

const PHILOSOPHY = [
  { title: "Business strategy comes first", body: "Every technology conversation starts with the business it is meant to serve, its strategy, its economics, its ambition. Technology decisions follow from there." },
  { title: "Technology serves the business", body: "Technology has no value on its own. It earns its place only when it advances a clear commercial objective." },
  { title: "Simplify, do not layer", body: "The best technology decisions take complexity out of the business. They rarely add to it." },
  { title: "Value you can measure", body: "Every investment should be judged by a result the business can point to, growth, margin, resilience or risk reduced." },
  { title: "Governance builds confidence", body: "Sound governance is what allows a leadership team to move quickly, and to move with certainty." },
  { title: "AI with a business purpose", body: "AI is worth pursuing where it solves a real business problem, and left alone where it does not." },
  { title: "Long-term over short-term", body: "Leadership decisions should reduce risk and support long-term growth, not chase the direction of the latest technology cycle." },
];

const VALUES = [
  { icon: Scale, title: "Independent executive perspective", body: "A senior, independent voice in the room when the decision matters. No vendor allegiance, no product to sell, only the right answer for the business." },
  { icon: Target, title: "Business-first technology strategy", body: "Every recommendation begins with the business result it is meant to create. Technology follows the strategy, never the other way around." },
  { icon: Handshake, title: "Vendor-neutral recommendations", body: "Objective evaluation of platforms, partners and providers, free of commercial bias or hidden incentives." },
  { icon: Landmark, title: "Technology governance", body: "The discipline and frameworks that keep major technology decisions accountable to the board and aligned to the business." },
  { icon: Compass, title: "Long-term strategic partnership", body: "A trusted advisor who stays through delivery, not a project that ends the day the system goes live." },
  { icon: Layers, title: "Executive decision support", body: "Clear counsel on the two or three technology decisions that most shape the next stage of the business." },
  { icon: BrainCircuit, title: "Practical AI strategy", body: "A grounded view of where AI creates real business value, and where it does not, with the governance to adopt it responsibly." },
  { icon: TrendingUp, title: "Business growth focus", body: "Technology decisions built around growth: capacity to scale, margin to protect, and competitive advantage to hold." },
];

const INDUSTRIES_ABOUT = [
  "Growing Businesses", "Mid-Market Companies", "Family-Owned Businesses", "Manufacturing",
  "Engineering", "Industrial Products", "Retail", "Healthcare",
  "Logistics", "Infrastructure",
];

const PRINCIPLES = [
  "Business before technology.",
  "Technology should create measurable value.",
  "Growth requires clarity.",
  "Governance reduces risk.",
  "Executive decisions should be based on business priorities.",
  "Technology should enable long-term competitive advantage.",
  "Partnership creates better outcomes than projects.",
];

const WHY_WORK = [
  "27+ years of executive leadership",
  "Business-first thinking",
  "Independent executive advice",
  "Fractional CIO leadership",
  "Executive technology strategy",
  "Practical digital transformation",
  "Global business perspective",
  "Trusted long-term advisor",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <>
      <Seo
        title="About, Executive Technology Advisor"
        description="Aftab Azad is an Executive Technology Advisor and Fractional CIO helping growing businesses, family-owned enterprises and executive leadership teams make better technology decisions across India, the UAE and the Gulf Region."
        path="/about"
        keywords={["Executive Technology Advisor", "Board Technology Advisor", "Enterprise Technology Leadership", "Fractional CIO", "Fractional CIO India", "Fractional CIO UAE", "Fractional CIO Gulf", "Family-Owned Business Advisor", "Mid-Market CIO", "AI Strategy Advisor"]}
      />
      <PageHeader
        overline="About Aftab Azad"
        titleLines={["Executive guidance", "for the next stage", "of growth."]}
        subtitle="Aftab Azad works with growing businesses, family-owned enterprises and executive leadership teams to make sound, business-first technology decisions across India, the UAE and the Gulf Region."
        testId="about-header"
      />

      {/* SECTION 1 — Intro */}
      <section data-testid="about-intro" className="bg-white py-20 md:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 md:px-12 lg:grid-cols-12">
          <div ref={ref} className="relative lg:col-span-5">
            <motion.div style={{ y }} className="overflow-hidden rounded-[2rem] border border-slate-200">
              <img src={PORTRAIT} alt="Aftab Azad speaking on enterprise transformation, AI, SAP and cloud strategy" className="h-[540px] w-full object-cover object-[30%_center] md:h-[620px]" />
            </motion.div>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Technology as a business decision</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Helping growing businesses", "make better technology", "decisions."]} />
            </h2>
            <div className="mt-6 max-w-[680px] space-y-4 font-body text-lg leading-relaxed text-slate-600">
              <p>
                Technology has become one of the most important decisions a leadership team makes.
                Where it once supported the business quietly in the background, it now shapes growth,
                cost, resilience and competitive position, the outcomes the board is measured on.
              </p>
              <p>
                Growing businesses reach a stage where expansion, operational complexity and rising
                customer expectations begin to demand stronger technology leadership. Yet many
                organisations do not need, or cannot justify, a full-time Chief Information Officer.
                They need experienced executive guidance.
              </p>
              <p>
                This is where Aftab Azad brings value. As an Executive Technology Advisor and Fractional
                CIO, he works alongside owners, chairmen, CEOs and managing directors to make sound,
                business-first technology decisions across India, the UAE and the Gulf Region.
              </p>
            </div>
            <ul className="mt-7 space-y-3">
              {CREDENTIALS.map((c) => (
                <li key={c} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.5} />
                  <span className="font-body text-slate-700">{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Leadership Philosophy */}
      <section data-testid="about-philosophy" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Leadership philosophy</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["How I think about", "technology."]} />
            </h2>
            <p className="mt-5 max-w-[680px] font-body text-lg leading-relaxed text-slate-600">
              A consistent set of convictions, built over decades of leading technology through real
              business consequence.
            </p>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} className="border-t border-slate-200 pt-5">
                <span className="font-body text-sm font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy">{p.title}</h3>
                <p className="mt-2 max-w-md font-body text-lg leading-relaxed text-slate-600">{p.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — Professional Journey */}
      <section data-testid="about-journey" className="bg-white py-20 md:py-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal className="overflow-hidden rounded-[2rem] border border-slate-200">
              <img src={IMG.boardroom} alt="Executive boardroom" className="h-[480px] w-full object-cover md:h-[560px]" />
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">The journey</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["From enterprise", "leadership to", "trusted advisor."]} />
            </h2>
            <div className="mt-6 max-w-[680px] space-y-4 font-body text-lg leading-relaxed text-slate-600">
              <p>
                Aftab&rsquo;s career was built inside the enterprise, leading technology through the
                realities of growth, complexity and change, not observing them from the outside.
              </p>
              <p>
                Over more than two decades in executive leadership, his responsibility widened from
                delivering enterprise systems to shaping the technology direction of the business.
                That path took him across business transformation, technology strategy, enterprise
                applications, governance and executive leadership, in each case answering the same
                practical question: what does the business actually need this decision to produce?
              </p>
              <p>
                Working across manufacturing, engineering, retail, healthcare, logistics and
                family-owned businesses, and across India, the UAE and the Gulf Region, has given
                him a straightforward understanding of what growth asks of a business, and where
                technology decisions most often help, or hurt.
              </p>
              <p>
                Today he brings that experience to leadership teams as an Executive Technology
                Advisor and Fractional CIO, the independent, senior voice organisations turn to
                when the decision has to be right.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — What Business Leaders Can Expect */}
      <section data-testid="about-values" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">What business leaders can expect</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The difference", "in the room."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} className="flex gap-5 border-t border-slate-200 pt-6">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl font-bold tracking-tight text-navy">{v.title}</h3>
                    <p className="mt-2 max-w-md font-body text-lg leading-relaxed text-slate-600">{v.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5 — Who I Work With */}
      <section data-testid="about-industries" className="bg-white py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Who I work with</p>
              <h2 className="mt-5 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
                <KineticHeadingInView lines={["Leadership teams", "preparing for the", "next stage of growth."]} />
              </h2>
              <p className="mt-6 max-w-md font-body text-lg leading-relaxed text-slate-600">
                Aftab primarily works with growing businesses, mid-market organisations and family-owned
                enterprises: the leadership teams making the technology decisions that will shape the next
                chapter of their business. Experience spans manufacturing, engineering, industrial
                products, retail, healthcare, logistics and infrastructure, with clients across India,
                the UAE and the Gulf Region.
              </p>
            </div>
            <div className="grid gap-px overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:col-span-7 lg:grid-cols-2">
              {INDUSTRIES_ABOUT.map((name) => (
                <div key={name} data-testid={`about-industry-${name.toLowerCase().replace(/\s|&/g, "-")}`} className="flex items-center bg-white p-7 transition-colors hover:bg-surface">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full bg-royal" />
                  <h3 className="ml-4 font-heading text-lg font-bold text-navy">{name}</h3>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Leadership Principles */}
      <section data-testid="about-principles" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Leadership principles</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["The principles", "behind the advice."]} />
            </h2>
          </div>
          <div className="mt-12 grid gap-x-10 gap-y-8 md:grid-cols-2">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p} className="relative border-t border-slate-200 pt-5">
                <span className="font-body text-sm font-bold text-royal">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-heading text-2xl font-bold tracking-tight text-navy">{p}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — Global Perspective */}
      <section data-testid="about-global" className="bg-white py-20 md:py-24">
        <div className="mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Global perspective</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.05] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["A broader lens", "on the business."]} />
            </h2>
            <div className="mt-6 max-w-[680px] space-y-4 font-body text-lg leading-relaxed text-slate-600">
              <p>
                Aftab&rsquo;s advisory work is anchored in India, the UAE and the Gulf Region, informed
                by experience across Singapore and global markets. That exposure sharpens judgement.
                Businesses operating under very different conditions still return to the same core
                questions, and the difference between a good and a bad answer becomes easier to see.
              </p>
              <p>
                For growing businesses, that means access to a perspective drawn from many similar
                decisions, one that helps leadership teams avoid the mistakes others have already
                paid for, and act with the confidence of a broader view.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2">
              {GEOGRAPHY.map((g, i) => (
                <span key={g} className="flex items-center gap-3">
                  <span className="font-heading text-sm font-semibold text-navy">{g}</span>
                  {i < GEOGRAPHY.length - 1 && <span className="h-1 w-1 rounded-full bg-royal/60" />}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5 lg:col-start-8">
            <Reveal className="overflow-hidden rounded-[2rem] border border-slate-200">
              <img src={IMG.architectureSingapore} alt="Global markets skyline" className="h-[440px] w-full object-cover md:h-[520px]" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Why Business Leaders Choose Aftab Azad */}
      <section data-testid="about-why" className="bg-surface py-20 md:py-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="max-w-2xl">
            <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-royal">Why business leaders choose Aftab Azad</p>
            <h2 className="mt-5 font-heading text-4xl font-light leading-[1.02] tracking-tight text-navy md:text-5xl">
              <KineticHeadingInView lines={["Trusted where", "it matters most."]} />
            </h2>
          </div>
          <div className="mt-10 grid gap-x-10 gap-y-5 md:grid-cols-2">
            {WHY_WORK.map((r) => (
              <div key={r} className="flex items-start gap-3 border-t border-slate-200 pt-5">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-navy" strokeWidth={1.5} />
                <span className="font-heading text-lg font-semibold text-navy">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — Closing Statement */}
      <section data-testid="about-closing" className="relative overflow-hidden bg-navy py-20 text-white md:py-24 grain-overlay">
        <div className="pointer-events-none absolute -left-32 top-1/2 h-[400px] w-[400px] -translate-y-1/2 rounded-full bg-royal/20 blur-[120px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center md:px-12">
          <p className="font-body text-xs font-bold uppercase tracking-[0.25em] text-sky">In closing</p>
          <blockquote className="mt-6 font-heading text-3xl font-light leading-[1.3] tracking-tight md:text-4xl">
            &ldquo;Technology decisions shape the future of every growing business. The right executive
            guidance helps organisations invest wisely, reduce risk and build technology capabilities
            that support long-term business success. My role is not to replace your leadership team.
            My role is to strengthen it.&rdquo;
          </blockquote>
          <p className="mt-8 font-signature text-4xl text-white">Aftab Azad</p>
          <p className="mt-1 font-body text-sm font-semibold uppercase tracking-[0.2em] text-slate-300">Executive Technology Advisor &amp; Fractional CIO</p>
        </div>
      </section>

      <CTASection
        overline="Let's talk"
        heading={["Let's discuss", "your business goals."]}
        body="Whether your business is expanding, modernising enterprise systems, evaluating AI opportunities or looking for experienced executive technology leadership, let's discuss how practical technology strategy can support your long-term growth."
        primary={{ label: "Schedule Executive Strategy Session", to: "/book" }}
        secondary={{ label: "Contact Aftab", to: "/contact" }}
      />
    </>
  );
}
